import React from 'react';
import { ArrowBack } from '@mui/icons-material';
import { Container, Grid, GridSize } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldValues, useForm } from 'react-hook-form';
import { useSnackbar } from '../../hooks/snackbar.hook';
import { AppButton } from '../../components/AppButton';
import { AppToolbar } from '../../components/AppToolbar';
import { HttpResponse } from '../../services/base.service';
import { AppFieldProps } from '../../types/components.type';
import { AppAutocompleteFieldProps } from '../../components/AppAutocompleteField';
import { AppFileUploadProps } from '../../components/AppFileUploadField';
import { AppTextFieldProps } from '../../components/AppTextField';
import { AppRadioGroupFieldProps } from '../../components/AppRadioGroup';

export type EditFormFieldType<T> = {
    name: keyof T,
    component: React.ElementType,
    extraControlValues?: string[];
    props?: {
        columns?: GridSize;
        getControlValue?: (value: any) => any;
        /* 
          Function to derive the value for a virtual field based on other form field values or external logic. 
          Useful for dynamically computed fields, like "is_single_station" from "workout_single_station".
        */
        getDerivedValue?: (formValues: T) => any;
    } & (Pick<AppFieldProps, 'label' | 'rules' | 'onChange'> | AppAutocompleteFieldProps | AppFileUploadProps | AppTextFieldProps | AppRadioGroupFieldProps)
}

export type EditFormComponentProps<T> = {
    title: string;
    mode?: 'edit' | 'create';
    service: {
        getById: (id: number) => Promise<HttpResponse<T>>;
        add: (data: T) => Promise<HttpResponse<T>>;
        update: (id: number, data: T) => Promise<HttpResponse<T>>;
    };
    formFields: EditFormFieldType<T>[];
    backPath: string;
    defaultValues?: Partial<T>;
    /**
     * Function to modify or clean the form data before submission (e.g., remove virtual fields).
     * @param formValues 
     * @returns 
     */
    transformPayload?: (formValues: T) => T;
};

export type EditFormRoutingParams = {
    id: string;
};

export default function AddOrUpdateComponent<T extends FieldValues>(props: EditFormComponentProps<T>) {
    const { mode = 'create', title, service, defaultValues, formFields, backPath: redirectPath, transformPayload } = props;
    const { id } = useParams<EditFormRoutingParams>();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { control, handleSubmit, setValue } = useForm<T>(defaultValues);

    React.useEffect(() => {
        if (mode === 'edit' && id) {
            service.getById(Number(id)).then(response => {
                const data = response.data;

                formFields.forEach((element: EditFormFieldType<T>) => {
                    if (element) {
                        const { getDerivedValue } = element.props || {};
                        if (data[element.name] !== null && data[element.name] !== undefined) {
                            let v = data[element.name];


                            if (element?.props?.getControlValue) {
                                v = element.props.getControlValue(data[element.name]);
                            }
                            setValue(element.name as any, v);
                        }

                        element.extraControlValues?.forEach((extraControl: string) => {
                            if (extraControl && data[extraControl] !== undefined) {
                                let v = data[extraControl];
                                setValue(extraControl as any, v);
                            }
                        });

                        // Check for derived values (virtual fields)
                        if (getDerivedValue) {
                            const derivedValue = getDerivedValue(data);  // Calculate derived value
                            setValue(element.name as any, derivedValue);
                        }

                    }
                });

            });
        }
    }, [mode, id, service, setValue, formFields]);

    const onSubmit = async (data: T) => {
        try {

            const payload = transformPayload ? transformPayload(data) : data;

            if (mode === 'create') {
                await service.add(payload);
            } else if (mode === 'edit' && id) {
                await service.update(Number(id), payload);
            }
            const action = mode === 'create' ? 'created' : 'updated';
            showSnackbar(`${title} ${action} successfully!`, 'success');
            navigate(redirectPath);
        } catch (error) {
            const msg = (error as any).response?.data?.message || 'An error occurred';
            console.error(msg, error);
            showSnackbar(msg, 'error');
        }
    };

    const handleBack = () => {
        navigate(redirectPath);
    };

    return (
        <Container>
            <AppToolbar title={mode === 'edit' ? `Edit ${title}` : `Add ${title}`}>
                <AppButton startIcon={<ArrowBack />} onClick={handleBack}>
                    Back
                </AppButton>
            </AppToolbar>
            <Container>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        {formFields.map((field, index) => {
                            const Component = field.component;
                            const { columns = 12, getDerivedValue, ...rest } = field.props || {};
                            return (
                                <Grid item xs={12} md={columns} key={index}>
                                    <Component
                                        key={index}
                                        name={field.name}
                                        control={control}
                                        setValue={setValue}
                                        {...rest}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                    <AppButton type="submit">
                        {mode === 'edit' ? 'Update' : 'Add'} {title}
                    </AppButton>
                </form>
            </Container>
        </Container>
    );
}
